import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import CareerPaths from '../../components/CareerPaths';

// ============================================================
// Mobile hook
// ============================================================

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
};

// ============================================================
// Типы
// ============================================================

interface CareerItem {
  career: string;
  score: number;
  reasons: string[];
}

interface SaturnPosition {
  sign: string;
  house: number;
}

interface VocationalResult {
  topCareers: CareerItem[];
  strengths: string[];
  challenges: string[];
  workStyle: string;
  aiInterpretation: string;
  mcSign?: string;
  tenthHouseSign?: string;
  saturnPosition?: SaturnPosition;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// ============================================================
// Константы
// ============================================================

const SIGN_NAMES_RU: Record<string, string> = {
  Aries: 'Овен',
  Taurus: 'Телец',
  Gemini: 'Близнецы',
  Cancer: 'Рак',
  Leo: 'Лев',
  Virgo: 'Дева',
  Libra: 'Весы',
  Scorpio: 'Скорпион',
  Sagittarius: 'Стрелец',
  Capricorn: 'Козерог',
  Aquarius: 'Водолей',
  Pisces: 'Рыбы',
};

const COLORS = {
  bg: '#0a0814',
  cardBg: 'rgba(13, 20, 40, 0.85)',
  cardBorder: 'rgba(212, 175, 55, 0.12)',
  cardShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  blue: '#2196F3',
  blueLight: '#64B5F6',
  orange: '#FF9800',
  orangeLight: '#FFB74D',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
};

const goldAccentLine = (
  <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
);

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

// ============================================================
// Компонент
// ============================================================

const VocationalPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [result, setResult] = useState<VocationalResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ApiResponse<VocationalResult>>('/vocational');
      if (res.data.success && res.data.data) {
        setResult(res.data.data);
      } else {
        setError(res.data.message || 'Не удалось загрузить анализ');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки анализа';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAnalysis();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.blue,
        fontSize: '18px',
      }}>
        Войдите, чтобы увидеть профориентацию
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.blue,
        fontSize: '18px',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>💼</div>
          <div>Анализируем профессиональное предназначение...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '40px auto',
        padding: '24px',
        background: 'rgba(255, 82, 82, 0.1)',
        border: `1px solid #FF525244`,
        borderRadius: '12px',
        color: '#FF5252',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Ошибка</div>
        <div>{error}</div>
        <button
          onClick={loadAnalysis}
          style={{
            marginTop: '16px',
            padding: '8px 20px',
            background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
            color: '#0A0814',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '40px auto',
        padding: '24px',
        textAlign: 'center',
        color: COLORS.textMuted,
      }}>
        Нет данных для анализа
      </div>
    );
  }

  const mcRu = result.mcSign ? SIGN_NAMES_RU[result.mcSign] || result.mcSign : null;
  const tenthRu = result.tenthHouseSign ? SIGN_NAMES_RU[result.tenthHouseSign] || result.tenthHouseSign : null;

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: isMobile ? '16px 12px' : '24px 16px',
      color: COLORS.text,
      position: 'relative',
    }}>
      {goldAccentLine}

      {/* Заголовок */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '32px' }}>
        <h1 style={{
          ...headingStyle,
          fontSize: isMobile ? '24px' : '32px',
          fontWeight: 700,
          margin: '0 0 8px 0',
        }}>
          💼 Вокционная астрология
        </h1>
        <p style={{ color: COLORS.textMuted, fontSize: isMobile ? '13px' : '15px', margin: 0 }}>
          Профессиональное предназначение и карьерные рекомендации
        </p>
        {(mcRu || tenthRu) && (
          <p style={{ color: COLORS.blueLight, fontSize: '14px', marginTop: '8px' }}>
            {mcRu && `MC: ${mcRu}`}
            {mcRu && tenthRu && ' • '}
            {tenthRu && `10-й дом: ${tenthRu}`}
            {result.saturnPosition && ` • Сатурн: ${SIGN_NAMES_RU[result.saturnPosition.sign] || result.saturnPosition.sign} (${result.saturnPosition.house} дом)`}
          </p>
        )}
      </div>

      {/* Career Paths Component */}
      <CareerPaths careers={result.topCareers} />

      {/* Сильные стороны */}
      {result.strengths.length > 0 && (
        <div style={{
          background: COLORS.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 16px 0',
          }}>
            💪 Сильные стороны
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {result.strengths.map((strength, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(33, 150, 243, 0.08)',
                  border: `1px solid ${COLORS.blue}33`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
              >
                {strength}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Вызовы */}
      {result.challenges.length > 0 && (
        <div style={{
          background: 'rgba(255, 152, 0, 0.08)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.orange}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 16px 0',
          }}>
            ⚡ Зоны роста
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
            {result.challenges.map((challenge, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255, 152, 0, 0.06)',
                  border: `1px solid ${COLORS.orange}33`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
              >
                {challenge}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Стиль работы */}
      {result.workStyle && (
        <div style={{
          background: COLORS.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 12px 0',
          }}>
            🎯 Ваш стиль работы
          </h2>
          <div style={{ color: COLORS.text, fontSize: '14px', lineHeight: 1.7 }}>
            {result.workStyle}
          </div>
        </div>
      )}

      {/* AI-интерпретация */}
      {result.aiInterpretation && (
        <div style={{
          background: COLORS.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '24px' : '32px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '20px',
            margin: '0 0 16px 0',
          }}>
            ✨ Интерпретация
          </h2>
          <div style={{ color: COLORS.text, fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {result.aiInterpretation}
          </div>
        </div>
      )}

      {/* Кнопка обновления */}
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <button
          onClick={loadAnalysis}
          disabled={loading}
          style={{
            padding: isMobile ? '10px 20px' : '10px 28px',
            background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
            color: '#0A0814',
            border: 'none',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: isMobile ? '13px' : '14px',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Загрузка...' : 'Обновить анализ'}
        </button>
      </div>
    </div>
  );
};

export default VocationalPage;
